import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Tipping1 from "@stackje/shared-components/cards/tipping/Tipping1";
import Tipping2 from "@stackje/shared-components/cards/tipping/Tipping2";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import { Label } from "@stackje/shared-components/ui/label";
import { Checkbox } from "@stackje/shared-components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@stackje/shared-components/ui/select";
import type { CommonColor } from "@stackje/common/config";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import { sampleData } from "@/components/add-card/constants";

// Zod schema for validation
const tippingCardFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
  paymentMethod: z.literal("paynow"),
  payeeInfo: z.object({
    paynow: z.object({
      type: z.enum(["MOBILE", "UEN"], { 
        errorMap: () => ({ message: "PayNow type is required" })
      }),
      value: z.string().min(1, "PayNow value is required"),
      recipientName: z.string().optional(),
    }),
  }).superRefine((payeeInfo, ctx) => {
    if (payeeInfo?.paynow?.value && payeeInfo?.paynow?.type) {
      const value = payeeInfo.paynow.value;
      const type = payeeInfo.paynow.type;
      
      if (type === "MOBILE") {
        if (!value.startsWith("+65")) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must start with Singapore country code +65",
            path: ["paynow", "value"],
          });
          return;
        }
        if (!/^\+65[0-9]{8}$/.test(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must be in format +65XXXXXXXX (8 digits after +65)",
            path: ["paynow", "value"],
          });
          return;
        }
      } else if (type === "UEN") {
        if (!/^[0-9]{9,10}[A-Z]$/.test(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "UEN must be 9-10 digits followed by a letter (e.g., 201912345K)",
            path: ["paynow", "value"],
          });
          return;
        }
      }
    }
  }),
  amounts: z.array(z.number().positive("Amount must be positive")),
  allowCustomAmount: z.boolean(),
  minAmount: z.number().positive("Minimum amount must be positive"),
  maxAmount: z.number().positive("Maximum amount must be positive"),
  currency: z.string(),
  buttonText: z.string(),
  thankYouNote: z.string(),
  type: z.enum(["tipping1", "tipping2"]),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
  }),
}).superRefine((data, ctx) => {
  // If custom amount is not enabled, must have at least one preset amount
  if (!data.allowCustomAmount && (!data.amounts || data.amounts.length === 0 || data.amounts.every(amount => amount <= 0))) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one preset amount is required when custom amount is disabled",
      path: ["amounts"],
    });
  }
});

export type TippingCardData = z.infer<typeof tippingCardFormSchema>;

interface TippingCardFormProps {
  initialData: TippingCardData;
  mode: "add" | "edit";
}

export function useTippingCardForm({ initialData, mode }: TippingCardFormProps) {
  const form = useForm<TippingCardData>({
    resolver: zodResolver(tippingCardFormSchema),
    defaultValues: initialData,
    mode: "all",
  });

  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors as CommonColor);

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const title = watchedValues.title;
  const description = watchedValues.description;
  const paymentMethod = watchedValues.paymentMethod;
  const payeeType = watchedValues.payeeInfo?.paynow?.type || "MOBILE";
  const payeeValue = watchedValues.payeeInfo?.paynow?.value || "";
  const recipientName = watchedValues.payeeInfo?.paynow?.recipientName || "";
  const amounts = watchedValues.amounts;
  const allowCustomAmount = watchedValues.allowCustomAmount;
  const minAmount = watchedValues.minAmount;
  const maxAmount = watchedValues.maxAmount;
  const currency = watchedValues.currency;
  const buttonText = watchedValues.buttonText;
  const thankYouNote = watchedValues.thankYouNote;
  const type = watchedValues.type;
  const isDirty = form.formState.isDirty;


  useEffect(() => {
    setColors(initialData.colors as CommonColor);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);


  const handlePayeeTypeChange = (newType: "MOBILE" | "UEN") => {
    form.setValue("payeeInfo.paynow.type", newType);
  };

  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType as "tipping1" | "tipping2");
    } else {
      // Only tipping1 is available for now, so no toggle action
      // form.setValue("type", type === "tipping1" ? "tipping2" : "tipping1");
    }
  };

  const handleAmountChange = (index: number, value: string) => {
    const newAmounts = [...amounts];
    const numValue = parseFloat(value) || 0;
    newAmounts[index] = numValue;
    form.setValue("amounts", newAmounts);
  };

  const addAmount = () => {
    form.setValue("amounts", [...amounts, 0]);
  };

  const removeAmount = (index: number) => {
    form.setValue("amounts", amounts.filter((_, i) => i !== index));
  };

  const formContent = (
    <>
      <div className="space-y-2">
        <div className="py-2">
          <h2 className="text-lg font-semibold">Tipping Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <ClearableInput
                  id="title"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="e.g., Buy Me a Teh Tarik! ☕"
                />
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="description">Description</Label>
          <Controller
            name="description"
            control={form.control}
            render={({ field }) => (
              <SimpleMarkdownEditor
                id="description"
                value={field.value}
                onChange={field.onChange}
                placeholder="Optional description for your tipping card"
              />
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label>Payment Method *</Label>
          <Controller
            name="paymentMethod"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paynow">PayNow QR (Singapore)</SelectItem>
                  </SelectContent>
                </Select>
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        {paymentMethod === "paynow" && (
          <>
            <div className="border-t pb-2 pt-4">
              <Label>PayNow Type *</Label>
              <Controller
                name="payeeInfo.paynow.type"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <Select value={field.value} onValueChange={(value) => {
                      field.onChange(value);
                      handlePayeeTypeChange(value as "MOBILE" | "UEN");
                    }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MOBILE">Mobile Number</SelectItem>
                        <SelectItem value="UEN">UEN</SelectItem>
                      </SelectContent>
                    </Select>
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </div>

            <div className="border-t pb-2 pt-4">
              <Label htmlFor="payeeValue">
                {payeeType === "MOBILE" ? "Mobile Number *" : "UEN *"}
              </Label>
              <Controller
                name="payeeInfo.paynow.value"
                control={form.control}
                render={({ field, fieldState }) => (
                  <>
                    <ClearableInput
                      id="payeeValue"
                      name="payeeValue"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={payeeType === "MOBILE" ? "+6591234567" : "201912345K"}
                    />
                    {payeeType === "MOBILE" && !fieldState.error && (
                      <p className="mt-1 text-sm text-gray-500">
                        Must include Singapore country code +65 (e.g., +6591234567)
                      </p>
                    )}
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">{fieldState.error.message}</p>
                    )}
                  </>
                )}
              />
            </div>

            <div className="border-t pb-2 pt-4">
              <Label htmlFor="recipientName">Recipient Name (Optional)</Label>
              <Controller
                name="payeeInfo.paynow.recipientName"
                control={form.control}
                render={({ field }) => (
                  <ClearableInput
                    id="recipientName"
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Display name for the recipient"
                  />
                )}
              />
            </div>
          </>
        )}

        <div className="border-t pb-2 pt-4">
          <Label>Preset Amounts {!allowCustomAmount && "*"}</Label>
          <div className="space-y-2">
            {amounts.map((amount, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1">
                  <ClearableInput
                    id={`amount-${index}`}
                    value={amount.toString()}
                    onChange={(value) => handleAmountChange(index, value)}
                    placeholder="Amount"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeAmount(index)}
                  className="px-2 py-1 text-sm text-red-600 hover:text-red-800"
                  disabled={amounts.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addAmount}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + Add Amount
            </button>
          </div>
          {form.formState.errors.amounts && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.amounts.message}</p>
          )}
        </div>

        <div className="border-t pb-2 pt-4">
          <div className="flex items-center space-x-2">
            <Controller
              name="allowCustomAmount"
              control={form.control}
              render={({ field }) => (
                <Checkbox
                  id="allowCustomAmount"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="allowCustomAmount">Allow Custom Amount</Label>
          </div>
        </div>

        {allowCustomAmount && (
          <>
            <div className="border-t pb-2 pt-4">
              <Label htmlFor="minAmount">Minimum Amount</Label>
              <Controller
                name="minAmount"
                control={form.control}
                render={({ field }) => (
                  <ClearableInput
                    id="minAmount"
                    value={field.value.toString()}
                    onChange={(value) => field.onChange(parseFloat(value) || 1)}
                    placeholder="1"
                  />
                )}
              />
            </div>

            <div className="border-t pb-2 pt-4">
              <Label htmlFor="maxAmount">Maximum Amount</Label>
              <Controller
                name="maxAmount"
                control={form.control}
                render={({ field }) => (
                  <ClearableInput
                    id="maxAmount"
                    value={field.value.toString()}
                    onChange={(value) => field.onChange(parseFloat(value) || 999)}
                    placeholder="999"
                  />
                )}
              />
            </div>
          </>
        )}

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="currency">Currency</Label>
          <Controller
            name="currency"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="currency"
                value={field.value}
                onChange={field.onChange}
                placeholder="SGD"
              />
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="buttonText">Button Text</Label>
          <Controller
            name="buttonText"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="buttonText"
                value={field.value}
                onChange={field.onChange}
                placeholder="Generate QR Code"
              />
            )}
          />
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="thankYouNote">Thank You Note</Label>
          <Controller
            name="thankYouNote"
            control={form.control}
            render={({ field }) => (
              <SimpleMarkdownEditor
                id="thankYouNote"
                value={field.value}
                onChange={field.onChange}
                placeholder="Thank you for your support! 🙏"
              />
            )}
          />
        </div>

        <input type="hidden" name="type" value={type} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />
        <input type="hidden" name="paymentMethod" value={paymentMethod} />
        <input type="hidden" name="payeeInfo.paynow.type" value={payeeType} />
        <input type="hidden" name="payeeInfo.paynow.value" value={payeeValue} />
        <input type="hidden" name="payeeInfo.paynow.recipientName" value={recipientName} />
        <input type="hidden" name="amounts" value={JSON.stringify(amounts)} />
        <input type="hidden" name="allowCustomAmount" value={allowCustomAmount.toString()} />
        <input type="hidden" name="minAmount" value={minAmount.toString()} />
        <input type="hidden" name="maxAmount" value={maxAmount.toString()} />
        <input type="hidden" name="thankYouNote" value={thankYouNote} />
      </div>
    </>
  );

  const getPreviewContent = () => {
    const previewData = isDirty || mode === "edit" ? {
      title,
      description,
      paymentMethod,
      payeeInfo: {
        paynow: {
          type: payeeType,
          value: payeeValue,
          recipientName: recipientName || undefined,
        }
      },
      amounts,
      allowCustomAmount,
      minAmount,
      maxAmount,
      currency,
      buttonText,
      thankYouNote,
      colors
    } : {
      title: sampleData.tipping1?.title || "Buy Me a Teh Tarik! ☕",
      description: sampleData.tipping1?.description || "Support my work with a donation",
      paymentMethod: "paynow" as const,
      payeeInfo: sampleData.tipping1?.payeeInfo || { paynow: { type: "MOBILE" as const, value: "+6591234567", recipientName: "John Doe" } },
      amounts: sampleData.tipping1?.amounts || [5, 10, 20, 50],
      allowCustomAmount: sampleData.tipping1?.allowCustomAmount ?? true,
      minAmount: sampleData.tipping1?.minAmount || 1,
      maxAmount: sampleData.tipping1?.maxAmount || 999,
      currency: sampleData.tipping1?.currency || "SGD",
      buttonText: sampleData.tipping1?.buttonText || "Generate PayNow QR",
      thankYouNote: sampleData.tipping1?.thankYouNote || "Thank you for your support! 🙏",
      colors
    };

    if (type === "tipping1") {
      return (
        <Tipping1
          title={previewData.title}
          description={previewData.description}
          paymentMethod={previewData.paymentMethod}
          payeeInfo={previewData.payeeInfo}
          amounts={previewData.amounts}
          allowCustomAmount={previewData.allowCustomAmount}
          minAmount={previewData.minAmount}
          maxAmount={previewData.maxAmount}
          currency={previewData.currency}
          buttonText={previewData.buttonText}
          thankYouNote={previewData.thankYouNote}
          colors={previewData.colors}
        />
      );
    } else {
      return (
        <Tipping2
          title={previewData.title}
          description={previewData.description}
          paymentMethod={previewData.paymentMethod}
          payeeInfo={previewData.payeeInfo}
          amounts={previewData.amounts}
          allowCustomAmount={previewData.allowCustomAmount}
          minAmount={previewData.minAmount}
          maxAmount={previewData.maxAmount}
          currency={previewData.currency}
          buttonText={previewData.buttonText}
          thankYouNote={previewData.thankYouNote}
          colors={previewData.colors}
        />
      );
    }
  };

  const previewContent = getPreviewContent();


  return {
    form,
    formContent,
    previewContent,
    toggleType,
    shuffleColors,
    isDirty,
    handleSubmit: form.handleSubmit,
    formData: {
      title,
      description,
      paymentMethod,
      payeeInfo: {
        paynow: {
          type: payeeType,
          value: payeeValue,
          recipientName: recipientName || undefined,
        }
      },
      amounts,
      allowCustomAmount,
      minAmount,
      maxAmount,
      currency,
      buttonText,
      thankYouNote,
      type,
      colors,
    } as TippingCardData,
  };
}